import { Camera, Globe, Lock, RotateCcw, Shield, Upload } from "lucide-react";
import { useImageCrop } from "../../hooks/useImageCrop";
import { Button, Input, Textarea } from "../ui";
import { ImageCropModal } from "../ui/ImageCropModal";
import type { ClubData, Privacy } from "./types";
import type { ClubSettingsForm } from "./useClubSettingsForm";

const privacyOptions: {
  value: Privacy;
  label: string;
  description: string;
  icon: typeof Globe;
}[] = [
  {
    value: "public",
    label: "Public",
    description: "Anyone can find and join",
    icon: Globe,
  },
  {
    value: "invite_only",
    label: "Invite Only",
    description: "Visible in discovery, requires an invite to join",
    icon: Shield,
  },
  {
    value: "private",
    label: "Private",
    description: "Hidden from discovery, requires an invite to join",
    icon: Lock,
  },
];

const privacyConfig = {
  public: { icon: Globe, label: "Public", color: "text-green-400" },
  private: { icon: Lock, label: "Private", color: "text-yellow-400" },
  invite_only: { icon: Shield, label: "Invite Only", color: "text-purple-400" },
} as const;

const IMAGE_CONFIG = {
  avatar: { maxFileSize: 2 * 1024 * 1024, outputWidth: 256, outputHeight: 256, aspect: 1 },
  banner: { maxFileSize: 5 * 1024 * 1024, outputWidth: 864, outputHeight: 384, aspect: 288 / 128 },
} as const;

type Props = {
  form: ClubSettingsForm;
  club: ClubData;
};

export function GeneralSection({ form, club }: Props) {
  const { getSrc, handleFileSelect, handleImageClick, cropModalProps } = useImageCrop(
    IMAGE_CONFIG,
    {
      avatar: {
        inputRef: form.avatarInputRef,
        preview: form.avatarPreview,
        displayUrl: (club.avatarUrl as string) || null,
        sourceUrl: (club.avatarSourceUrl as string) || null,
        cropData: form.avatarCropData,
        setPendingSourceFile: form.setPendingAvatarSourceFile,
        setPendingBlob: form.setPendingAvatarBlob,
        setPreview: form.setAvatarPreview,
        setCropData: form.setAvatarCropData,
      },
      banner: {
        inputRef: form.bannerInputRef,
        preview: form.bannerPreview,
        displayUrl: (club.bannerUrl as string) || null,
        sourceUrl: (club.bannerSourceUrl as string) || null,
        cropData: form.bannerCropData,
        setPendingSourceFile: form.setPendingBannerSourceFile,
        setPendingBlob: form.setPendingBannerBlob,
        setPreview: form.setBannerPreview,
        setCropData: form.setBannerCropData,
      },
    },
  );

  const bannerSrc = getSrc("banner");
  const avatarSrc = getSrc("avatar");

  const privacy = privacyConfig[form.privacy];
  const PrivacyIcon = privacy.icon;

  return (
    <div className="space-y-8">
      {/* Name & Description */}
      <div className="space-y-4">
        <Input
          label="Club Name"
          value={form.name}
          onChange={(e) => form.setName(e.target.value)}
          maxLength={50}
          error={form.errors.name}
        />
        <Textarea
          label="Description"
          value={form.description}
          onChange={(e) => form.setDescription(e.target.value)}
          maxLength={500}
          rows={3}
          resize="none"
          placeholder="What's your club about?"
        />
      </div>

      {/* Privacy */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-content-secondary">Privacy</label>
        <div className="grid gap-2">
          {privacyOptions.map((opt) => {
            const Icon = opt.icon;
            const selected = form.privacy === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => form.setPrivacy(opt.value)}
                className={`group flex items-center gap-3.5 px-4 py-3 rounded-xl border text-left transition-all duration-200 ${
                  selected
                    ? "border-brand-500/40 bg-brand-500/5 shadow-[inset_0_1px_0_color-mix(in_srgb,var(--color-brand-500)_10%,transparent)]"
                    : "border-surface-700/40 bg-surface-800/30 hover:border-surface-600 hover:bg-surface-800/60"
                }`}
              >
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                    selected
                      ? "bg-brand-500/15 text-brand-400"
                      : "bg-surface-700/50 text-surface-500 group-hover:text-surface-400"
                  }`}
                >
                  <Icon className="size-4" />
                </div>
                <div className="min-w-0">
                  <p
                    className={`text-sm font-semibold transition-colors ${selected ? "text-white" : "text-surface-300"}`}
                  >
                    {opt.label}
                  </p>
                  <p className="text-xs text-surface-500">{opt.description}</p>
                </div>
                <div
                  className={`ml-auto w-4 h-4 rounded-full border-2 transition-all shrink-0 ${
                    selected
                      ? "border-brand-500 bg-brand-500 shadow-[0_0_8px_color-mix(in_srgb,var(--color-brand-500)_40%,transparent)]"
                      : "border-surface-600"
                  }`}
                >
                  {selected && (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-surface-900" />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Branding */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-content-secondary">Branding</label>

        {/* Hidden file inputs */}
        <input
          ref={form.bannerInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleFileSelect("banner")}
          className="hidden"
        />
        <input
          ref={form.avatarInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleFileSelect("avatar")}
          className="hidden"
        />

        <div className="flex gap-6 items-start">
          {/* Sidebar preview */}
          <div className="shrink-0">
            <span className="text-2xs font-medium text-surface-500 uppercase tracking-wider mb-2 block">
              Sidebar preview
            </span>
            <div className="w-72 rounded-lg overflow-hidden border border-surface-700/50 bg-surface-850">
              {/* Banner */}
              <button
                type="button"
                onClick={() => handleImageClick("banner")}
                className="group relative w-full h-32 overflow-hidden cursor-pointer"
              >
                {bannerSrc ? (
                  <img src={bannerSrc} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-surface-850" />
                    <div className="absolute inset-0 bg-brand-500/[0.07]" />
                    <div
                      className="absolute inset-0 opacity-[0.06]"
                      style={{
                        backgroundImage: `
                          linear-gradient(60deg, transparent 48.5%, rgba(38,217,181,0.5) 49%, rgba(38,217,181,0.5) 51%, transparent 51.5%),
                          linear-gradient(-60deg, transparent 48.5%, rgba(38,217,181,0.5) 49%, rgba(38,217,181,0.5) 51%, transparent 51.5%)
                        `,
                        backgroundSize: "60px 100px",
                      }}
                    />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-surface-850 via-surface-850/40 to-transparent" />
                <div className="absolute inset-0 noise-overlay" />
                <div className="absolute inset-0 bg-surface-950/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-2">
                  <Camera className="size-4 text-white" />
                  <span className="text-xs text-white/80 font-medium">
                    {bannerSrc ? "Adjust banner" : "Upload banner"}
                  </span>
                </div>
              </button>

              {/* Avatar + name */}
              <div className="px-5 -mt-8 relative z-10 pb-3">
                <div className="flex items-end justify-between">
                  <button
                    type="button"
                    onClick={() => handleImageClick("avatar")}
                    className="group relative w-16 h-16 rounded-xl overflow-hidden ring-[3px] ring-surface-850 shadow-lg shadow-black/30 shrink-0 cursor-pointer"
                  >
                    {avatarSrc ? (
                      <img src={avatarSrc} alt="" className="size-full object-cover" />
                    ) : (
                      <div className="size-full bg-surface-800 border border-surface-700/50 flex items-center justify-center text-white font-bold text-2xl">
                        {club.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-surface-950/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                      <Camera className="size-4 text-white" />
                    </div>
                  </button>
                  <div
                    className={`flex items-center gap-1 px-1.5 py-0.5 -mr-2 rounded text-2xs font-bold uppercase tracking-wider bg-surface-800/80 border border-surface-700/40 ${privacy.color}`}
                  >
                    <PrivacyIcon className="size-2.5" />
                    {privacy.label}
                  </div>
                </div>
                <h3 className="text-lg font-bold text-white leading-tight line-clamp-1 min-w-0 mt-3">
                  {club.name}
                </h3>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex-1 space-y-3 pt-6">
            {/* Banner controls */}
            <div className="flex items-center justify-between rounded-lg bg-surface-900/50 border border-surface-700/30 px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-surface-200">Banner</p>
                <p className="text-2xs text-surface-500">864 × 384px, max 5 MB</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {bannerSrc && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="!px-2 !py-0.5 !text-xs"
                    leftIcon={<Camera className="size-3" />}
                    onClick={() => handleImageClick("banner")}
                  >
                    Adjust
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="!px-2 !py-0.5 !text-xs"
                  leftIcon={
                    bannerSrc ? <RotateCcw className="size-3" /> : <Upload className="size-3" />
                  }
                  onClick={() => form.bannerInputRef.current?.click()}
                >
                  {bannerSrc ? "Replace" : "Upload"}
                </Button>
              </div>
            </div>

            {/* Avatar controls */}
            <div className="flex items-center justify-between rounded-lg bg-surface-900/50 border border-surface-700/30 px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-surface-200">Avatar</p>
                <p className="text-2xs text-surface-500">256 × 256px, max 2 MB</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {avatarSrc && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="!px-2 !py-0.5 !text-xs"
                    leftIcon={<Camera className="size-3" />}
                    onClick={() => handleImageClick("avatar")}
                  >
                    Adjust
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="!px-2 !py-0.5 !text-xs"
                  leftIcon={
                    avatarSrc ? <RotateCcw className="size-3" /> : <Upload className="size-3" />
                  }
                  onClick={() => form.avatarInputRef.current?.click()}
                >
                  {avatarSrc ? "Replace" : "Upload"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Crop Modal */}
      {cropModalProps && <ImageCropModal {...cropModalProps} />}
    </div>
  );
}
